import axios from 'axios';

// Create axios instance
const client = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Wire up request and response interceptors with access to the Redux store.
 * Called once after the store is created in store/index.js.
 *
 * @param {import('@reduxjs/toolkit').Store} store
 * @param {{ logout: Function, showInsufficientCreditsModal: Function }} actions
 */
export const setupInterceptors = (store, { logout, showInsufficientCreditsModal }) => {
  // Request interceptor — attach JWT from Redux store
  client.interceptors.request.use(
    (config) => {
      const token = store.getState().auth.token;
      if (token) {
        config.headers["X-User-Token"] = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor — global error handling
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      // Network error (no response)
      if (!error.response) {
        return Promise.reject({
          code: 'NETWORK_ERROR',
          message: 'Network error. Please check your internet connection.',
          details: error.message,
        });
      }

      const { status, data } = error.response;

      if (status === 401 && data?.code === 'SESSION_EXPIRED') {
        store.dispatch(logout());
        window.location.replace('/login?expired=true');
        return Promise.reject({ code: 'SESSION_EXPIRED', message: 'Session expired.' });
      }

      if (status === 403 && data?.code === 'INSUFFICIENT_CREDITS') {
        store.dispatch(showInsufficientCreditsModal());
      }

      if (status === 429) {
        return Promise.reject({
          code: 'RATE_LIMITED',
          message: data?.detail?.message || 'You have made too many requests. Please try again later.',
        });
      }

      // Standardised error format
      return Promise.reject({
        code: data?.detail?.code ?? null,
        message: data?.detail?.message || 'An unexpected error occurred.',
        status,
      });
    }
  );
};

export default client;
